import { useMemo } from 'react';
import { vacationEligibility, type VacationEligibility } from '@domain/policies/TenurePolicy';
import { getServices } from '@infrastructure/services';
import { useSessionState } from '../providers/SessionProvider';

/**
 * Whether the viewer may file a vacation request yet.
 *
 * Separate from `useAccess`, which answers what the permission matrix allows.
 * This answers a question about the person: the right to take vacation vests on
 * completing one year of service, counted anniversary to anniversary, and
 * `resolveEntitlement` deliberately shows the legal minimum from day one — so
 * without this a new hire sees twelve days and a button that invites them to
 * spend them.
 *
 * Read from the `people` record rather than the session, because `Session`
 * carries no hire date; it is placement and role, not history.
 *
 * The same rule is enforced in `submitVacationRequest`, which is the one that
 * counts. This is what stops somebody reaching the end of a four-step wizard to
 * be told no.
 */
export const useVacationEligibility = (): VacationEligibility => {
  const { status } = useSessionState();
  const hireDate = status.kind === 'ready' ? status.person.hireDate : null;

  return useMemo(
    () => vacationEligibility({ hireDate, asOf: getServices().clock.today() }),
    [hireDate],
  );
};
